import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { endpoints, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/authContext";
import { useFan } from "@/lib/fanContext";
import { toast } from "sonner";
import { MapPin, ExternalLink, CalendarDays, Users, Ticket, Globe, ArrowRight, Heart } from "lucide-react";
import { formatDate } from "@/lib/format";

export default function VenuePage() {
    const { id } = useParams();
    const nav = useNavigate();
    const [params, setParams] = useSearchParams();
    const { user } = useAuth();
    const { fan, isFollowingVenue, followVenue, unfollowVenue } = useFan();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const load = () => {
        if (!id) return;
        setLoading(true);
        endpoints.venuePublic(id)
            .then((d) => {
                setData(d);
                try {
                    document.title = `${d.venue.name} · ${d.venue.city || ""} · Project Mint`;
                    const setMeta = (n, c) => {
                        let m = document.querySelector(`meta[name="${n}"]`);
                        if (!m) { m = document.createElement("meta"); m.setAttribute("name", n); document.head.appendChild(m); }
                        m.setAttribute("content", c || "");
                    };
                    setMeta("description", d.venue.description || `${d.venue.name} — venue in ${d.venue.city || ""}, ${d.venue.country || ""}.`);
                } catch {}
            })
            .catch((e) => setErr(formatApiError(e)))
            .finally(() => setLoading(false));
    };
    useEffect(load, [id]);

    // Auto-follow on return from sign-in (?follow=1)
    useEffect(() => {
        if (!fan || !id) return;
        if (params.get("follow") !== "1") return;
        if (data && data.venue.is_following) {
            const next = new URLSearchParams(params); next.delete("follow"); setParams(next, { replace: true });
            return;
        }
        (async () => {
            try {
                await followVenue(id, "venue_page");
                toast.success(`Following ${data?.venue.name || "venue"}`);
                setData((d) => d ? { ...d, venue: { ...d.venue, is_following: true, followers_count: (d.venue.followers_count || 0) + 1 } } : d);
            } catch { /* ignore */ }
            const next = new URLSearchParams(params); next.delete("follow"); setParams(next, { replace: true });
        })();
    }, [fan, data?.venue?.id, id, params]);

    const toggleFollow = async () => {
        if (!user || !fan) {
            const nextUrl = `/venues/${id}?follow=1`;
            nav(`/signin?next=${encodeURIComponent(nextUrl)}`);
            return;
        }
        setBusy(true);
        try {
            if (isFollowingVenue(id) || data?.venue?.is_following) {
                await unfollowVenue(id);
                setData((d) => d ? { ...d, venue: { ...d.venue, is_following: false, followers_count: Math.max(0, (d.venue.followers_count || 1) - 1) } } : d);
                toast(`Unfollowed ${data?.venue?.name}`);
            } else {
                await followVenue(id, "venue_page");
                setData((d) => d ? { ...d, venue: { ...d.venue, is_following: true, followers_count: (d.venue.followers_count || 0) + 1 } } : d);
                toast.success(`Following ${data?.venue?.name}`);
            }
        } catch {
            toast.error("Could not update follow.");
        } finally { setBusy(false); }
    };

    if (loading) return <div className="p-8 md:p-16 font-mono text-xs text-[#666]" data-testid="venue-public-loading">Loading venue…</div>;
    if (err || !data) return (
        <div className="max-w-2xl mx-auto p-8 md:p-16" data-testid="venue-public-notfound">
            <div className="border border-[#2a2a2a] p-8">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff3333]">Not available</div>
                <h1 className="mt-2 font-display font-black uppercase text-3xl">Venue unavailable</h1>
                <p className="mt-3 text-sm text-[#a3a3a3]">This venue isn't publicly available right now.</p>
                <Link to="/venues" className="pm-btn mt-6 inline-flex" data-testid="venue-public-directory">← Browse venues</Link>
            </div>
        </div>
    );

    const { venue: v, upcoming_events = [], past_events = [], artists = [] } = data;
    const cover = v.cover_url || v.image_url;

    return (
        <div className="pm-fade" data-testid="venue-public-page">
            <section className="relative border-b border-[#2a2a2a]">
                <div className="relative h-[42vh] md:h-[58vh] min-h-[320px] overflow-hidden">
                    {cover && <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover grayscale opacity-70" />}
                    <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/40 via-transparent to-[#0a0a0a]" />
                </div>
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 -mt-24 md:-mt-32 relative z-10 pb-8">
                    <div className="flex items-end gap-4">
                        {v.image_url && (
                            <img src={v.image_url} alt="" className="w-24 h-24 md:w-32 md:h-32 object-cover grayscale border border-[#2a2a2a]" data-testid="venue-public-logo" />
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Venue</div>
                            <h1 className="mt-1 font-display font-black uppercase tracking-tighter text-4xl md:text-6xl leading-[0.9]" data-testid="venue-public-name">{v.name}</h1>
                            <div className="mt-3 flex flex-wrap gap-4 text-sm text-[#a3a3a3]">
                                {(v.city || v.country) && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {[v.city, v.country].filter(Boolean).join(", ")}</span>}
                                {v.capacity ? <span className="flex items-center gap-1"><Users className="w-4 h-4" /> Capacity {v.capacity}</span> : null}
                                {v.address && <span className="hidden md:inline text-[#666]">· {v.address}</span>}
                                <span className="flex items-center gap-1 text-[#f4f4f0]" data-testid="venue-public-follower-count"><Heart className="w-4 h-4 text-[#ff4400]" /> {v.followers_count || 0} follower{(v.followers_count || 0) === 1 ? "" : "s"}</span>
                            </div>
                        </div>
                        <button
                            onClick={toggleFollow}
                            disabled={busy}
                            className={`pm-btn ${v.is_following || isFollowingVenue(id) ? "" : "pm-btn-primary"} whitespace-nowrap`}
                            data-testid="venue-public-follow-btn"
                        >
                            <Heart className="w-4 h-4 mr-2" fill={(v.is_following || isFollowingVenue(id)) ? "currentColor" : "none"} />
                            {(v.is_following || isFollowingVenue(id)) ? "Following" : "Follow"}
                        </button>
                    </div>

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="md:col-span-2">
                            {v.description ? (
                                <p className="text-lg leading-relaxed text-[#f4f4f0]" data-testid="venue-public-description">{v.description}</p>
                            ) : (
                                <p className="text-sm text-[#a3a3a3]">A Project Mint venue.</p>
                            )}
                        </div>
                        <div className="border border-[#2a2a2a] p-4 space-y-2 h-fit">
                            {v.website && (
                                <a href={v.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs font-mono uppercase text-[#ff4400] hover:underline" data-testid="venue-public-website">
                                    <Globe className="w-3 h-3" /> {v.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                                    <ExternalLink className="w-3 h-3 opacity-60" />
                                </a>
                            )}
                            {(v.social_links || []).map((s, i) => (
                                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs font-mono uppercase text-[#a3a3a3] hover:text-[#ff4400]" data-testid={`venue-public-social-${i}`}>
                                    {s.platform} <ExternalLink className="w-3 h-3 opacity-60" />
                                </a>
                            ))}
                            {(!v.website && (!v.social_links || v.social_links.length === 0)) && (
                                <div className="text-[10px] font-mono uppercase text-[#666]">No public links yet.</div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <section className="border-b border-[#2a2a2a]">
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-8 md:py-12">
                    <div className="flex items-baseline justify-between mb-4">
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Live</div>
                            <h2 className="font-display font-black uppercase tracking-tighter text-2xl md:text-4xl">Upcoming events</h2>
                        </div>
                        <div className="text-xs font-mono uppercase text-[#a3a3a3]" data-testid="venue-public-upcoming-count">{upcoming_events.length}</div>
                    </div>
                    {upcoming_events.length === 0 ? (
                        <div className="border border-dashed border-[#2a2a2a] p-8 text-center text-[#a3a3a3]" data-testid="venue-public-upcoming-empty">No upcoming events scheduled.</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                            {upcoming_events.map((e) => <EventCard key={e.id} e={e} testId={`venue-public-upcoming-${e.id}`} />)}
                        </div>
                    )}
                </div>
            </section>

            {past_events.length > 0 && (
                <section className="border-b border-[#2a2a2a]">
                    <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-8 md:py-12">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Archive</div>
                        <h2 className="font-display font-black uppercase tracking-tighter text-2xl md:text-4xl mb-4">Past events</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                            {past_events.slice(0, 12).map((e) => <EventCard key={e.id} e={e} past testId={`venue-public-past-${e.id}`} />)}
                        </div>
                    </div>
                </section>
            )}

            {artists.length > 0 && (
                <section className="border-b border-[#2a2a2a]">
                    <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-8 md:py-12">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Alumni</div>
                        <h2 className="font-display font-black uppercase tracking-tighter text-2xl md:text-4xl mb-4">Artists who've performed here</h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                            {artists.map((a) => (
                                <Link key={a.id} to={`/artists/${a.id}`} className="group bg-[#0a0a0a] p-4 hover:bg-[#141414] transition-colors" data-testid={`venue-public-artist-${a.id}`}>
                                    <div className="relative aspect-square overflow-hidden">
                                        <img src={a.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" />
                                    </div>
                                    {a.genre && <div className="mt-2 text-[10px] font-mono uppercase text-[#ff4400] truncate">{a.genre}</div>}
                                    <div className="font-display font-bold uppercase text-sm truncate">{a.name}</div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}

function EventCard({ e, past, testId }) {
    return (
        <Link to={`/events/${e.id}`} className="group bg-[#0a0a0a] p-4 flex flex-col hover:bg-[#141414] transition-colors" data-testid={testId}>
            <div className="relative aspect-video overflow-hidden">
                {e.image_url ? (
                    <img src={e.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" />
                ) : <div className="absolute inset-0 bg-[#141414]" />}
            </div>
            <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#ff4400] flex items-center gap-1">
                <CalendarDays className="w-3 h-3" /> {formatDate(e.date)} · {e.start_time || ""}
            </div>
            <div className="mt-1 font-display font-black uppercase text-lg leading-tight">{e.name}</div>
            <div className="mt-1 text-xs text-[#a3a3a3] truncate">{e.artist_name}</div>
            {!past && (
                <div className="mt-2 text-[10px] font-mono uppercase text-[#a3a3a3]">
                    <Ticket className="w-3 h-3 inline mr-1" />
                    {e.is_free ? "Free entry" : e.is_pay_at_door ? "Pay at door" : e.ticket_categories?.[0]?.price ? `From $${e.ticket_categories[0].price}` : "Ticketed"}
                </div>
            )}
            <span className="mt-3 inline-flex items-center gap-1 text-[10px] font-mono uppercase text-[#f4f4f0] group-hover:text-[#ff4400]">
                View event <ArrowRight className="w-3 h-3" />
            </span>
        </Link>
    );
}
